import * as React from 'react'
import * as PropertiesPanel from '../../properties-panel'
import {
  type Block,
  type CodeSnippetBlock,
} from '@basehub/data/replicache/index'
import { LANGUAGES } from '@basehub/shared-constants'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { clsx } from 'clsx'
import { useEditorMode } from '../../editor-mode-provider'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useTabsStore } from '../../tabs/store'

export const CodeSnippetBlockPropertiesPanel = ({
  block,
  parent,
  path,
}: {
  block: CodeSnippetBlock
  parent: Block
  path: string
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const editorMode = useEditorMode()
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const getInputFocusOp = React.useCallback(
    (id: string) => {
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:properties-panel',
        elementIdOverride: id,
      })
    },
    [block.id, block.sourceBlockId, activeTabKey]
  )

  const options = Object.entries(LANGUAGES)
    .sort((a, b) => {
      return a[1].label.localeCompare(b[1].label)
    })
    .map(([key, value]) => {
      return {
        name: value.label,
        id: key,
      }
    })

  const checkedOptions =
    block.constraints.allowedLanguages === null
      ? options // null is "all languages"
      : block.constraints.allowedLanguages
          .map((key) => {
            return options.find((option) => option.id === key)
          })
          .filter(Boolean)

  const sortedOptions = options.sort((a, b) => {
    if (editorMode !== 'write:all') {
      // we'll pin the selected options to the top
      if (checkedOptions.includes(a) && checkedOptions.includes(b)) {
        return 0
      } else if (checkedOptions.includes(a)) return -1
      else if (checkedOptions.includes(b)) return 1
      else return 0
    }
    return 0
  })

  return (
    <PropertiesPanel.PropertiesContainer>
      <PropertiesPanel.Group title="Constraints" spacing="tight">
        {editorMode !== 'write:all' ? (
          checkedOptions.length ? (
            <ul className="flex flex-wrap gap-4">
              {checkedOptions.map((option) => (
                <li key={option.id}>
                  <p className={clsx('cursor-default text-dark-bright')}>
                    {option.name}
                  </p>
                </li>
              ))}
            </ul>
          ) : null
        ) : (
          <PropertiesPanel.CheckList
            options={sortedOptions}
            checkedOptions={checkedOptions}
            title="Languages"
            id={`el-${sourceBlockId}-pp-languages`}
            listClassName="max-h-[360px] overflow-y-auto border border-dark-border-solid rounded pl-2"
            mutate={async (checked, newOptions) => {
              let newAcceptedTypes: string[] | null = []
              if (
                sortedOptions.length ===
                block.constraints.allowedLanguages?.length
              ) {
                newAcceptedTypes = null
              } else if (checked === 'overwrite') {
                newAcceptedTypes = newOptions.map((o) => o.id)
              } else if (block.constraints.allowedLanguages === null) {
                newAcceptedTypes = checked
                  ? null
                  : // null is "all languages", so we start from options and filter out the one we want to remove
                    options
                      .filter((o) => !newOptions.some((n) => n.id === o.id))
                      .map((o) => o.id)
              } else {
                // else we use current selected ones
                newAcceptedTypes = checked
                  ? block.constraints.allowedLanguages.concat(
                      newOptions.map((o) => o.id)
                    )
                  : block.constraints.allowedLanguages.filter(
                      (t) => !newOptions.some((n) => n.id === t)
                    )
              }

              const params: UpdateBlockParams = {
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                data: {
                  type: block.type,
                  constraints: { allowedLanguages: newAcceptedTypes },
                },
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-languages`),
                    { op: 'updateBlock', args: params },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-languages`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            }}
          />
        )}
      </PropertiesPanel.Group>

      <PropertiesPanel.CommonSearchConfigGroup
        block={block}
        parent={parent}
        path={path}
      />
    </PropertiesPanel.PropertiesContainer>
  )
}
